import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, MapPin, Star, Navigation, Wifi, DoorOpen, UserCircle2, Images, CalendarPlus, CalendarDays, MessageSquare, MessageCircle, Ticket, Tag, Store, ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMiniSiteContext } from "@/lib/minisiteContext";
import BookingModal from "@/components/booking/BookingModal";
import SurveyModal from "@/components/booking/SurveyModal";
import WaitlistModal from "@/components/booking/WaitlistModal";
import PropertyMap from "@/components/property/PropertyMap";
import FloorPlanViewer from "@/components/property/FloorPlanViewer";
import NearbyAuto from "@/components/property/NearbyAuto";
import AmenityDisplay from "@/components/property/AmenityDisplay";
import { AmenityIcon } from "@/components/property/amenityMeta";
import PropertyReviews from "@/components/review/PropertyReviews";
import FavoriteButton from "@/components/property/FavoriteButton";
import SanquaBadge from "@/components/property/SanquaBadge";
import { photoSrc } from "@/lib/photos";
import { ROOM_STATUS_LABELS, ROOM_STATUS_STYLES, formatRupiah, resolveRoomPrice, pickAvailablePeriod, PERIOD_PRICE_KEY, PERIOD_SUFFIX } from "@/components/property/roomMeta";
import { discountLabel, formatPromoDate } from "@/components/promo/promoMeta";

// WhatsApp Click-to-Chat: normalize Indonesian numbers to 62xxxxxxxxxx.
function waLink(phone) {
  if (!phone) return null;
  let d = String(phone).replace(/[^0-9]/g, "");
  if (!d) return null;
  if (d.startsWith("0")) d = "62" + d.slice(1);
  else if (d.startsWith("62")) { /* already intl */ }
  else if (d.startsWith("8")) d = "62" + d;
  return `https://wa.me/${d}`;
}

function Section({ icon: Icon, title, testid, children }) {
  return (
    <section className="mt-8" data-testid={testid}>
      <h2 className="flex items-center gap-2 font-heading text-xl font-semibold">
        <Icon size={18} className="text-primary" /> {title}
      </h2>
      <div className="mt-3 rounded-2xl border border-dashed border-border bg-secondary/30 p-6 text-sm text-muted-foreground leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const minisite = useMiniSiteContext();
  const preferredPeriod = new URLSearchParams(window.location.search).get("price_period") || "month";
  const PERIOD_WORD = { day: "per hari", month: "per bulan", year: "per tahun" };
  const [amenities, setAmenities] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [floorPlan, setFloorPlan] = useState([]);
  const availableRooms = rooms.filter((r) => r.status === "available");
  const [photos, setPhotos] = useState([]);
  const [roomPhotos, setRoomPhotos] = useState({});
  const [activePhoto, setActivePhoto] = useState(null);
  const [bookingRoom, setBookingRoom] = useState(null);
  const [bookingPromoCode, setBookingPromoCode] = useState(null);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [surveyRoom, setSurveyRoom] = useState(null);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistRoom, setWaitlistRoom] = useState(null);
  const [availablePromos, setAvailablePromos] = useState([]);
  const [ratingSummary, setRatingSummary] = useState(null);
  const [favorited, setFavorited] = useState(false);
  const [property, setProperty] = useState(null);

  const handleBook = (room, promoCode = null) => {
    if (user && user.role !== "customer") { toast.error("Hanya customer yang dapat melakukan booking."); return; }
    setBookingPromoCode(promoCode);
    setBookingRoom(room);
  };

  const openSurvey = (room = null) => {
    if (!user) { toast.info("Silakan masuk sebagai customer untuk menjadwalkan survei."); navigate("/login"); return; }
    if (user.role !== "customer") { toast.error("Hanya customer yang dapat menjadwalkan survei."); return; }
    setSurveyRoom(room);
    setSurveyOpen(true);
  };

  const openWaitlist = (room) => {
    if (!user) { toast.info("Silakan masuk sebagai customer untuk gabung waiting list."); navigate("/login"); return; }
    if (user.role !== "customer") { toast.error("Hanya customer yang dapat gabung waiting list."); return; }
    setWaitlistRoom(room);
    setWaitlistOpen(true);
  };

  const handleUsePromo = (promo) => {
    const firstAvailable = rooms.find((r) => r.status === "available");
    if (!firstAvailable) { toast.error("Tidak ada kamar tersedia untuk promo ini."); return; }
    handleBook(firstAvailable, promo.promo_code);
  };

  const contactOwner = async () => {
    if (!user) { toast.info("Silakan masuk sebagai customer untuk menghubungi owner."); navigate("/login"); return; }
    if (user.role !== "customer") { toast.error("Hanya customer yang dapat menghubungi owner."); return; }
    try {
      const { data } = await apiClient.post("/conversations", { property_id: id });
      navigate(`/customer/messages?c=${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  useEffect(() => {
    apiClient.get(`/properties/${id}/location`)
      .then((r) => setProperty(r.data || null))
      .catch(() => setProperty(null));
    apiClient.get(`/properties/${id}/amenities`)
      .then((r) => setAmenities(r.data || []))
      .catch(() => {});
    apiClient.get(`/properties/${id}/rooms`)
      .then((r) => setRooms(r.data || []))
      .catch(() => {});
    apiClient.get(`/properties/${id}/floorplan/public`, { params: { floor: 1 } })
      .then((r) => setFloorPlan(r.data?.elements || []))
      .catch(() => setFloorPlan([]));
    apiClient.get(`/properties/${id}/photos`)
      .then((r) => { const ps = r.data || []; setPhotos(ps); setActivePhoto(ps.find((p) => p.is_cover) || ps[0] || null); })
      .catch(() => {});
    apiClient.get(`/properties/${id}/room-photos`)
      .then((r) => setRoomPhotos(r.data || {}))
      .catch(() => {});
    apiClient.get(`/properties/${id}/promos`)
      .then((r) => setAvailablePromos(r.data || []))
      .catch(() => {});
    apiClient.get(`/properties/${id}/reviews`)
      .then((r) => setRatingSummary(r.data?.summary || null))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!user || user.role !== "customer") { setFavorited(false); return; }
    apiClient.get("/favorites/ids")
      .then((r) => setFavorited((r.data?.property_ids || []).includes(id)))
      .catch(() => {});
  }, [id, user]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="property-detail-page">
      <button
        onClick={() => (minisite?.slug ? navigate(`/properti/${minisite.slug}`) : navigate(-1))}
        className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-6"
        data-testid="detail-back-button"
      >
        <ArrowLeft size={16} /> {minisite?.slug ? `Kembali ke ${minisite.owner_name || "web-site"}` : "Kembali"}
      </button>

      {/* Gallery */}
      {photos.length > 0 ? (
        <div data-testid="detail-gallery">
          <div className="rounded-[2rem] overflow-hidden border border-border bg-secondary/30 aspect-[16/7]">
            <img src={photoSrc(activePhoto)} alt="Foto properti" className="w-full h-full object-cover" data-testid="detail-gallery-main" />
          </div>
          {photos.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="detail-gallery-thumbs">
              {photos.map((p, i) => (
                <button key={p.id} onClick={() => setActivePhoto(p)} data-testid={`detail-gallery-thumb-${i}`}
                  className={`shrink-0 w-24 h-16 rounded-xl overflow-hidden border-2 transition-colors ${activePhoto?.id === p.id ? "border-primary" : "border-transparent"}`}>
                  <img src={photoSrc(p)} alt="thumbnail" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-[2rem] overflow-hidden border border-dashed border-border bg-secondary/30 aspect-[16/7] grid place-items-center" data-testid="detail-gallery-placeholder">
          <div className="text-center text-muted-foreground">
            <Images size={28} className="mx-auto mb-2 text-primary" />
            <p className="text-sm">Foto belum tersedia</p>
          </div>
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Properti #{id}</span>
          <div className="mt-3 flex items-start justify-between gap-4">
            <h1 className="font-heading text-3xl font-bold tracking-tight">Detail Hunian</h1>
            <FavoriteButton
              propertyId={id}
              initialFavorited={favorited}
              onToggled={(_, isFav) => setFavorited(isFav)}
              variant="detail"
              className="shrink-0"
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-muted-foreground items-center">
            <span className="inline-flex items-center gap-1.5" data-testid="detail-header-location"><MapPin size={16} /> {property ? [property.city_name, property.province_name].filter(Boolean).join(", ") || "Lokasi" : "Lokasi"}</span>
            <span className="inline-flex items-center gap-1.5" data-testid="detail-header-rating">
              <Star size={16} className="text-amber-400" fill="currentColor" />
              {ratingSummary && ratingSummary.review_count > 0
                ? `${(ratingSummary.breakdown?.overall || 0).toFixed(1)} · ${ratingSummary.review_count} ulasan`
                : "Belum ada ulasan"}
            </span>
            {property?.sanqua_partner && <SanquaBadge testid="detail-sanqua-badge" />}
          </div>

          <Section icon={MapPin} title="Lokasi" testid="detail-location">
            <PropertyMap
              latitude={property?.latitude}
              longitude={property?.longitude}
              address={property?.address}
              addressParts={property}
            />
          </Section>

          <Section icon={Navigation} title="Tempat Terdekat" testid="detail-nearby">
            <NearbyAuto propertyId={id} />
          </Section>

          <Section icon={Wifi} title="Fasilitas (Amenities)" testid="detail-amenities">
            <AmenityDisplay amenities={amenities} testid="detail-amenity-display" />
          </Section>

          {availablePromos.length > 0 && (
            <section className="mt-8" data-testid="detail-promos-section">
              <h2 className="flex items-center gap-2 font-heading text-xl font-semibold">
                <Ticket size={18} className="text-primary" /> Promo Tersedia
              </h2>
              <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="detail-promos-list">
                {availablePromos.map((promo, i) => (
                  <div key={promo.id} data-testid={`detail-promo-${i}`}
                    className="relative overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/5 to-accent/5 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-heading text-base font-semibold truncate">{promo.promo_name}</p>
                        {promo.description && <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{promo.description}</p>}
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="font-heading text-2xl font-bold text-primary leading-none" data-testid={`detail-promo-discount-${i}`}>{discountLabel(promo)}</p>
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">Diskon Kamar</p>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1 font-mono font-bold text-primary bg-primary/10 border border-dashed border-primary/40 rounded-md px-2 py-0.5" data-testid={`detail-promo-code-${i}`}>
                        <Tag size={11} /> {promo.promo_code}
                      </span>
                      {promo.minimum_booking_amount > 0 && <span>Min. {formatRupiah(promo.minimum_booking_amount)}</span>}
                      <span>· s/d {formatPromoDate(promo.end_date)}</span>
                    </div>

                    <button onClick={() => handleUsePromo(promo)} data-testid={`detail-promo-use-${i}`}
                      className="mt-3 w-full py-2 rounded-full text-sm font-semibold bg-primary text-primary-foreground hover:scale-[1.02] active:scale-95 transition-transform">
                      Gunakan Promo
                    </button>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Kode promo akan otomatis diterapkan saat booking. Diskon final dihitung ulang oleh sistem.</p>
            </section>
          )}

          <Section icon={DoorOpen} title="Kamar (Rooms)" testid="detail-rooms">
            {floorPlan.length > 0 && (
              <div className="mb-5" data-testid="detail-floorplan">
                <p className="text-sm font-semibold mb-2">Denah Kamar</p>
                <FloorPlanViewer
                  elements={floorPlan}
                  rooms={rooms}
                  roomPhotos={roomPhotos}
                  preferredPeriod={preferredPeriod}
                  onBook={handleBook}
                  onSurvey={openSurvey}
                  onWaitlist={openWaitlist}
                />
              </div>
            )}
            {availableRooms.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="detail-rooms-empty">Belum ada kamar tersedia.</p>
            ) : (
              <div className="space-y-3 -m-1" data-testid="detail-rooms-list">
                {availableRooms.map((r, i) => (
                  <div key={r.id} data-testid={`detail-room-${i}`}
                    className="rounded-xl border border-border bg-card p-4 flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-foreground">{r.room_number}</span>
                        {r.name && <span className="text-sm text-muted-foreground">· {r.name}</span>}
                        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${ROOM_STATUS_STYLES[r.status] || ""}`} data-testid={`detail-room-status-${i}`}>
                          {ROOM_STATUS_LABELS[r.status] || r.status}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        {(() => {
                          const rp = resolveRoomPrice(r, preferredPeriod);
                          return rp.unit ? (
                            <span data-testid={`detail-room-price-${i}`}><span className="font-semibold text-primary">{rp.label}</span> {rp.suffix}</span>
                          ) : (
                            <span data-testid={`detail-room-price-${i}`}>Harga belum tersedia</span>
                          );
                        })()}
                        {r.size != null && <span>{r.size} m²</span>}
                        {r.floor != null && <span>Lantai {r.floor}</span>}
                      </div>
                      {r.amenities?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {r.amenities.map((a) => (
                            <span key={a.id} className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/40 px-2.5 py-1 text-xs">
                              <AmenityIcon name={a.icon} size={12} className="text-primary" /> {a.name}
                            </span>
                          ))}
                        </div>
                      )}
                      {roomPhotos[r.id]?.length > 0 && (
                        <div className="mt-2 flex gap-2 overflow-x-auto" data-testid={`detail-room-photos-${i}`}>
                          {roomPhotos[r.id].slice(0, 6).map((ph) => (
                            <img key={ph.id} src={photoSrc(ph)} alt="foto kamar"
                              className="shrink-0 w-20 h-16 rounded-lg object-cover border border-border" />
                          ))}
                        </div>
                      )}
                    </div>
                    {r.status === "available" && (
                      <div className="shrink-0 flex flex-col sm:flex-row gap-2">
                        <button onClick={() => handleBook(r)} data-testid={`detail-room-book-${i}`}
                          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-accent text-accent-foreground hover:scale-105 active:scale-95 transition-transform">
                          <CalendarPlus size={15} /> Booking
                        </button>
                        <button onClick={() => openSurvey(r)} data-testid={`detail-room-survey-${i}`}
                          className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border border-primary/40 text-primary hover:bg-primary/5 active:scale-95 transition-[transform,background-color]">
                          <CalendarDays size={15} /> Jadwalkan Survei
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section icon={UserCircle2} title="Pemilik (Owner)" testid="detail-owner">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-muted-foreground">Hubungi pemilik untuk menanyakan ketersediaan atau detail hunian.</p>
              <div className="flex items-center gap-2 flex-wrap" data-testid="detail-owner-contacts">
                <button onClick={contactOwner} data-testid="detail-contact-owner"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-105 active:scale-95 transition-transform">
                  <MessageSquare size={16} /> Hubungi Owner
                </button>
                {(property?.owner_whatsapp_contacts || []).map((c, i) => {
                  const link = waLink(c.phone);
                  if (!link) return null;
                  return (
                    <a key={i} href={link} target="_blank" rel="noopener noreferrer" data-testid={`detail-owner-wa-${i}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 text-white font-semibold hover:scale-105 active:scale-95 transition-transform">
                      <MessageCircle size={16} /> {c.label}
                    </a>
                  );
                })}
              </div>
            </div>

            {property?.owner_minisite_slug && (
              <Link
                to={`/properti/${property.owner_minisite_slug}`}
                data-testid="detail-owner-minisite-link"
                className="group mt-4 flex items-center justify-between gap-3 rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/5 to-accent/5 p-4 hover:border-primary/50 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="grid place-items-center w-11 h-11 shrink-0 rounded-xl bg-primary/10 text-primary">
                    <Store size={20} />
                  </span>
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-sm sm:text-base truncate">
                      Cari hunian sejenis dari {property.owner_name || "pemilik ini"}?
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Lihat portofolio lengkap di web-site pemilik.
                    </p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 shrink-0 text-sm font-semibold text-primary">
                  <span className="hidden sm:inline">Lihat</span>
                  <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            )}
          </Section>

          <Section icon={Star} title="Ulasan Penghuni" testid="detail-reviews-section">
            <PropertyReviews propertyId={id} />
          </Section>
        </div>

        {/* Booking CTA sidebar */}
        <aside className="lg:col-span-1">
          {(() => {
            const activeRooms = availableRooms;
            const minGetter = (u) => {
              const key = PERIOD_PRICE_KEY[u];
              const vals = activeRooms.map((r) => r[key]).filter((v) => v != null && Number(v) > 0);
              return vals.length ? Math.min(...vals) : null;
            };
            const mins = { day: minGetter("day"), month: minGetter("month"), year: minGetter("year") };
            const present = ["month", "day", "year"].filter((u) => mins[u] != null);
            const primary = pickAvailablePeriod((u) => mins[u], preferredPeriod);
            const others = present.filter((u) => u !== primary);
            return (
              <div className="bg-card border border-border rounded-2xl p-6 lg:sticky lg:top-24" data-testid="detail-booking-sidebar">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Mulai dari</p>
                <p className="mt-1 font-heading text-3xl font-bold" data-testid="detail-min-price">
                  {primary ? formatRupiah(mins[primary]) : "Rp —"}
                </p>
                <p className="text-sm text-muted-foreground" data-testid="detail-min-price-period">{primary ? PERIOD_WORD[primary] : "per bulan"}</p>
                {others.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border space-y-1" data-testid="detail-other-prices">
                    {others.map((u) => (
                      <p key={u} className="text-sm" data-testid={`detail-price-${u}`}>
                        <span className="font-semibold text-foreground">{formatRupiah(mins[u])}</span>
                        <span className="text-muted-foreground"> {PERIOD_WORD[u]}</span>
                      </p>
                    ))}
                  </div>
                )}
                {(() => {
                  const firstAvailable = rooms.find((r) => r.status === "available");
                  return (
                    <>
                      {firstAvailable && (
                        <button
                          onClick={() => firstAvailable && handleBook(firstAvailable)}
                          className="mt-6 w-full py-3 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform"
                          data-testid="detail-book-button"
                        >
                          Booking Kamar
                        </button>
                      )}
                      {!firstAvailable && (
                        <div className="mt-6 w-full py-3 rounded-full bg-secondary text-muted-foreground font-semibold text-center text-sm" data-testid="detail-book-unavailable">
                          Tidak ada kamar tersedia
                        </div>
                      )}
                      <p className="mt-3 text-xs text-center text-muted-foreground">Belum ada pembayaran pada tahap ini.</p>
                    </>
                  );
                })()}
              </div>
            );
          })()}
        </aside>
      </div>

      <BookingModal
        open={!!bookingRoom}
        room={bookingRoom}
        availableRooms={availableRooms}
        propertyId={id}
        initialPromoCode={bookingPromoCode}
        initialUnit={bookingRoom ? (resolveRoomPrice(bookingRoom, preferredPeriod).unit || "month") : "month"}
        onClose={() => { setBookingRoom(null); setBookingPromoCode(null); }}
        onSuccess={() => {
          apiClient.get(`/properties/${id}/rooms`).then((r) => setRooms(r.data || [])).catch(() => {});
          apiClient.get(`/properties/${id}/promos`).then((r) => setAvailablePromos(r.data || [])).catch(() => {});
        }}
      />

      <SurveyModal
        open={surveyOpen}
        propertyId={id}
        room={surveyRoom}
        onClose={() => { setSurveyOpen(false); setSurveyRoom(null); }}
        onSuccess={() => toast.success("Jadwal survei berhasil diajukan.")}
      />

      <WaitlistModal
        open={waitlistOpen}
        propertyId={id}
        propertyName={property?.name}
        room={waitlistRoom}
        onClose={() => { setWaitlistOpen(false); setWaitlistRoom(null); }}
        onSuccess={() => toast.success("Anda berhasil masuk waiting list.")}
      />
    </div>
  );
}
